import { create } from "zustand";
import {
  getDayState,
  submitPlan as submitPlanApi,
  submitEod as submitEodApi
} from "@/services/EmployeeService/DailyPlanService";
import { registerStoreReset } from "@/store/storeResetUtils";

export type TaskStatus =
  | 'planned'
  | 'completed'
  | 'not_completed'
  | 'dropped'
  | 'carried';

export interface PlanTask {
  id: number;
  plan_date?: string;
  attendance_report_id?: number | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  incomplete_reason: string | null;
  drop_reason: string | null;
  carried_from_task_id: number | null;
  sort_order: number;
}

export interface PlanWindow {
  withinWindow: boolean;
  opensAt: string;
  nowMinutes: number;
  opensAtMinutes: number;
}

export interface DayState {
  serverNow: string;
  timezone: string;
  today: string;
  isWorkingToday: boolean;
  window: PlanWindow;
  planSubmitted: boolean;
  eodSubmittedToday: boolean;
  pendingRetroEod: { date: string; attendanceId: number; tasks: PlanTask[] } | null;
  /** Total prior days still owing an EOD, including the one in pendingRetroEod. */
  pendingRetroEodCount: number;
  carryOverTasks: PlanTask[];
  todayTasks: PlanTask[];
  gateRequired: 'retro_eod' | 'plan' | null;
}

export interface SubmitPlanPayload {
  tasks: { title: string; description?: string }[];
  carryOver: { originalTaskId: number; action: 'continue' | 'drop'; dropReason?: string }[];
  remarks?: string;
}

export interface SubmitEodPayload {
  forDate?: string;
  taskStatuses: { taskId: number; status: 'completed' | 'not_completed'; incompleteReason?: string }[];
  remarks?: string;
}

interface DailyPlanStore {
  dayState: DayState | null;
  loading: boolean;
  submitting: boolean;
  error: string | null;

  fetchDayState: () => Promise<void>;
  submitPlan: (payload: SubmitPlanPayload) => Promise<boolean>;
  submitEod: (payload: SubmitEodPayload) => Promise<boolean>;
  clearError: () => void;
  resetStore: () => void;
}

const useDailyPlanStore = create<DailyPlanStore>((set) => ({
  dayState: null,
  loading: false,
  submitting: false,
  error: null,

  fetchDayState: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getDayState();
      if (response.success) {
        set({ dayState: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to load day state', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  submitPlan: async (payload: SubmitPlanPayload) => {
    try {
      set({ submitting: true, error: null });
      const response = await submitPlanApi(payload);
      if (response.success) {
        // Response data is the fresh day state.
        set({ dayState: response.data, submitting: false });
        return true;
      }
      set({ error: response.error?.message || 'Failed to submit plan', submitting: false });
      return false;
    } catch (error: any) {
      set({ error: error.message, submitting: false });
      return false;
    }
  },

  submitEod: async (payload: SubmitEodPayload) => {
    try {
      set({ submitting: true, error: null });
      const response = await submitEodApi(payload);
      if (response.success) {
        set({ dayState: response.data, submitting: false });
        return true;
      }
      set({ error: response.error?.message || 'Failed to submit EOD report', submitting: false });
      return false;
    } catch (error: any) {
      set({ error: error.message, submitting: false });
      return false;
    }
  },

  clearError: () => set({ error: null }),

  resetStore: () => set({ dayState: null, loading: false, submitting: false, error: null })
}));

// Register this store for centralized reset on logout / session switch.
registerStoreReset('dailyPlan', () => useDailyPlanStore.getState().resetStore());

export default useDailyPlanStore;
